import { MediaConstants } from 'src/constants';
import { ValidationError } from 'src/errors/CustomErrors';
import { withTimeout } from 'src/utils/MediaUtils';

import { loadVideoDimensions } from '../MediaUtils';
import { DimensionSpecs, VideoLengthSpecs } from '../Types';
import { validateAspectRatio, validateDimensions } from '../ValidationUtils';

/**
 * Validates the length of a video file against specifications
 *
 * @param fileObject - The video file to validate
 * @param specs - Specifications for validation
 * @returns Promise that resolves with video duration if validation passes, throws ValidationError otherwise
 */
export const validateVideoLength = (
  fileObject: File,
  specs: VideoLengthSpecs,
  timeoutMs = MediaConstants.TIMEOUTS.MEDIA_VIDEO_LOAD,
): Promise<number> => {
  // Return a rejected promise for file type errors instead of throwing synchronously
  if (!fileObject.type.startsWith(MediaConstants.MIME.PREFIXES.VIDEO)) {
    return Promise.reject(
      new ValidationError(`File type ${fileObject.type} is not a video format`),
    );
  }

  const video = document.createElement(MediaConstants.ELEMENTS.VIDEO);
  const objectUrl = URL.createObjectURL(fileObject);

  const loadPromise = new Promise<number>((resolve, reject) => {
    video.onloadedmetadata = () => {
      try {
        const duration = video.duration;
        if (isNaN(duration) || duration <= 0) {
          reject(new ValidationError('Invalid video duration'));
          return;
        }
        if (
          specs.maxDurationInSeconds &&
          duration >
            specs.maxDurationInSeconds +
              MediaConstants.VALIDATION.VIDEO_DURATION_PRECISION_TOLERANCE
        ) {
          reject(
            new ValidationError(
              `Video duration (${Math.round(duration)}s) exceeds maximum allowed duration of ${specs.maxDurationInSeconds}s`,
            ),
          );
          return;
        }
        if (
          duration <
          specs.minDurationInSeconds - MediaConstants.VALIDATION.VIDEO_DURATION_PRECISION_TOLERANCE
        ) {
          reject(
            new ValidationError(
              `Video duration (${Math.round(duration)}s) is less than minimum required duration of ${specs.minDurationInSeconds}s`,
            ),
          );
          return;
        }
        resolve(duration);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };

    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new ValidationError('Failed to load video'));
    };

    video.preload = MediaConstants.VIDEO.DEFAULT_PRELOAD;
    video.src = objectUrl;
  });

  return withTimeout(loadPromise, timeoutMs);
};

/**
 * Validates video dimensions against specifications
 * @throws {ValidationError} When dimensions don't meet requirements
 */
export const validateVideoDimensions = async (
  videoFile: File,
  specs: DimensionSpecs,
): Promise<void> => {
  if (!videoFile.type.startsWith(MediaConstants.MIME.PREFIXES.VIDEO)) {
    throw new ValidationError('File is not a video');
  }

  const objectUrl = URL.createObjectURL(videoFile);
  try {
    const dimensions = await loadVideoDimensions(objectUrl);
    if (dimensions.width === 0 || dimensions.height === 0) {
      throw new ValidationError('Could not determine file dimensions');
    }
    validateDimensions(dimensions.width, dimensions.height, specs);
    if (specs?.aspectRatio != null && specs?.aspectRatioTolerance != null) {
      validateAspectRatio(
        dimensions.width,
        dimensions.height,
        specs.aspectRatio,
        specs.aspectRatioTolerance,
      );
    }
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
